import React from 'react';
import { FullscreenOverlayRoot } from './FullscreenOverlayRoot';
import { FullscreenOverlayTrigger } from './FullscreenOverlayTrigger';
import { FullscreenOverlayPortal } from './FullscreenOverlayPortal';
import { FullscreenOverlayBackdrop } from './FullscreenOverlayBackdrop';
import { FullscreenOverlayContent } from './FullscreenOverlayContent';
import { FullscreenOverlayHeader } from './FullscreenOverlayHeader';
import { FullscreenOverlayBody } from './FullscreenOverlayBody';
import { FullscreenOverlayFooter } from './FullscreenOverlayFooter';
import type { FullscreenOverlayProps } from './types';

/**
 * FullscreenOverlay Component (Shorthand API)
 *
 * Simplified API for common fullscreen overlay usage.
 * Provides sensible defaults and variant-specific behavior.
 *
 * Supports three layout variants:
 * - 'standard': Traditional modal with sticky header + body + footer
 * - 'media': Full-screen media with floating controls (no footer)
 *
 * @example Standard usage (current Spoke behavior)
 * <FullscreenOverlay
 *   variant="standard"
 *   trigger={<Button>Open</Button>}
 *   title="Amenities"
 *   headerActions={<ShareButton />}
 *   footer={<ApplyButton />}
 * >
 *   <AmenitiesContent />
 * </FullscreenOverlay>
 *
 * @example Media usage (immersive pattern)
 * <FullscreenOverlay
 *   variant="media"
 *   trigger={<Button>View Photos</Button>}
 * >
 *   <PropertyPhotos />
 * </FullscreenOverlay>
 */
export const FullscreenOverlay = ({
  variant = 'standard',
  trigger,
  title,
  headerActions,
  headerLayout,
  contentPadding,
  footer,
  footerVariant,
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  containerId,
}: FullscreenOverlayProps) => {
  // Determine defaults based on variant
  const actualHeaderLayout = headerLayout ?? (variant === 'standard' ? 'sticky' : 'floating');
  const actualContentPadding = contentPadding ?? (variant === 'media' ? 'edge-to-edge' : 'standard');
  const actualFooterVariant = footerVariant ?? 'equal-width';

  return (
    <FullscreenOverlayRoot open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <FullscreenOverlayTrigger asChild>{trigger}</FullscreenOverlayTrigger>

      <FullscreenOverlayPortal containerId={containerId}>
        <FullscreenOverlayBackdrop>
          <FullscreenOverlayContent variant={variant}>
            <FullscreenOverlayHeader
              title={title}
              actions={headerActions}
              layout={actualHeaderLayout}
              variant={variant}
            />

            <FullscreenOverlayBody padding={actualContentPadding} variant={variant}>
              {children}
            </FullscreenOverlayBody>

            {footer && (
              <FullscreenOverlayFooter variant={actualFooterVariant} overlayVariant={variant}>
                {footer}
              </FullscreenOverlayFooter>
            )}
          </FullscreenOverlayContent>
        </FullscreenOverlayBackdrop>
      </FullscreenOverlayPortal>
    </FullscreenOverlayRoot>
  );
};
